import React from 'react';
import { Field, ErrorMessage } from 'formik';
import dayjs from 'dayjs';
import DateField from '../common/DateField ';
import NumberField from '../common/NumberField';

/**
 * CommonLabFields - Reusable component for common lab fields that appear at the top of test results
 * 
 * @param {Object} props - Component props
 * @param {Object} props.values - Formik values object
 * @param {Function} props.setFieldValue - Formik setFieldValue function
 * @param {boolean} props.showSampleId - Whether to show Sample ID field (default: true when SampleAvailability is Present)
 * @param {string} props.className - Additional CSS classes for the container
 */
const CommonLabFields = ({
  values,
  setFieldValue,
  showSampleId = values?.SampleAvailability === "Present",
    testType = "",
  className = ""
}) => {
  const todayString = dayjs().format('YYYY-MM-DD');

  return (
    <div className={`row ${className}`}>
      {/* Lab Serial Number - Optional */}
      <div className="col-md-6 mt-3">
        <div className="form-group">
          <label htmlFor="LabSerialNumber" className="form-label">
            Lab Serial Number
          </label>
          <Field 
            id="LabSerialNumber" 
            name="LabSerialNumber" 
            placeholder="Enter Lab Serial Number" 
            className="form-control input-width" 
          />
          <ErrorMessage name="LabSerialNumber" component="div" className="text-danger" />
        </div>
      </div>

      {/* Induration Size - Show only for Cy-Tb test type */}
      {values.TestType === "Cy-Tb" && (
        <div className="col-md-6 mt-3">
          <NumberField
            name="IndurationSize"
            label="Induration Size (in mm)"
            placeholder="Enter Induration Size (in mm)"
          />
        </div>
      )}

      {/* Sample ID - Conditional on Sample Availability and showSampleId prop */}
      {showSampleId && (
        <div className="col-md-6 mt-3">
          <div className="form-group has-error grid-item">
            <label htmlFor="ResultSampleId" className="form-label">
              <span style={{ color: "red", fontSize: "20px" }}>*</span>
              Sample ID
            </label>
            <Field
              as="select"
              id="ResultSampleId"
              name="ResultSampleId"
              data-required="true"
              className="form-control input-width select"
            >
              <option disabled hidden value="">Select Sample ID</option>
              {values.TestSampleDetails?.map((_, index) => {
                const sampleNum = String(index + 1);
                return (
                  <option key={sampleNum} value={sampleNum}>
                    Sample-{sampleNum}
                  </option>
                );
              })}
            </Field>
            <ErrorMessage name="ResultSampleId" component="div" className="text-danger" />
          </div>
        </div>
      )}

      {/* Date Tested - Optional */}
      <div className="col-md-6 mt-3">
        <DateField
          name="ResultDateTested"
          label="Date Tested"
          required={false}
          max={todayString}
        />
      </div>

      {/* Date Reported - Required, must be on or after Date Tested */}
      <div className="col-md-6 mt-3">
        <DateField
          name="ResultDateReported"
          label="Date Reported"
          required={true}
          max={todayString}
          min={
            // Convert stored DD-MM-YYYY → YYYY-MM-DD for the min constraint
            values.ResultDateTested && dayjs(values.ResultDateTested, 'DD-MM-YYYY').isValid()
              ? dayjs(values.ResultDateTested, 'DD-MM-YYYY').format('YYYY-MM-DD')
              : undefined
          }
        />
      </div>

      {/* Reported By - Optional */}
      <div className="col-md-6 mt-3">
        <div className="form-group">
          <label htmlFor="ResultReportedBy" className="form-label">
            Reported By
          </label>
          <Field 
            id="ResultReportedBy" 
            name="ResultReportedBy" 
            placeholder="Enter Reported By" 
            className="form-control input-width" 
          />
          <ErrorMessage name="ResultReportedBy" component="div" className="text-danger" />
        </div>
      </div>
    </div>
  );
};

export default CommonLabFields;